import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { cva } from "class-variance-authority";
import * as React from "react";
import { type FC, type SVGProps } from "react";
import ChevronDownIcon from "@/icons/__generated__/ChevronDown/ChevronDown20";
import { cn } from "@/lib/utils";

// Add this type assertion after the ChevronDownIcon import
const ChevronDown = ChevronDownIcon as unknown as FC<SVGProps<SVGSVGElement>>;

export type NavigationMenuContextValue = {
  variant: "white" | "black";
  layout: "fullWidth" | "tabStrip";
};

const NavigationMenuContext = React.createContext<NavigationMenuContextValue>({
  variant: "white",
  layout: "fullWidth",
});

type NavigationMenuProps = React.ComponentPropsWithoutRef<
  typeof NavigationMenuPrimitive.Root
> & {
  variant?: "white" | "black";
  /** `fullWidth`: site header bar. `tabStrip`: fixed-width listing tab row. Defaults to `fullWidth` when omitted. */
  layout?: "fullWidth" | "tabStrip";
};

const NavigationMenu = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Root>,
  NavigationMenuProps
>(
  (
    { className, children, variant = "white", layout = "fullWidth", ...props },
    ref,
  ) => (
    <NavigationMenuContext.Provider value={{ variant, layout }}>
      <NavigationMenuPrimitive.Root
        ref={ref}
        className={cn(
          "tw-relative tw-z-[1030] group",
          layout === "tabStrip"
            ? "tw-box-border tw-flex tw-w-full tw-max-w-[1440px] tw-items-center tw-pt-16 tw-pb-0 tw-pl-24 tw-pr-24"
            : "tw-flex tw-min-h-[72px] tw-w-full tw-max-w-full tw-items-center tw-px-24",
          {
            "tw-bg-brand-white tw-text-brand-black [&_a:hover]:tw-text-gray-700":
              variant === "white",
            "tw-bg-brand-black tw-text-brand-white [&_a:hover]:tw-text-gray-500":
              variant === "black",
          },
          className,
        )}
        {...props}
      >
        <span
          itemScope
          itemType="http://schema.org/Organization"
          className="tw-pointer-events-none tw-absolute tw-left-0 tw-top-0 tw-h-px tw-w-px tw-overflow-hidden"
        >
          <link itemProp="url" href="http://www.peerspace.com/" />
          <meta itemProp="sameAs" content="http://www.facebook.com/Peerspace" />
          <meta itemProp="sameAs" content="http://www.x.com/Peerspace" />
          <meta itemProp="sameAs" content="http://www.pinterest.com/Peerspace" />
          <meta
            itemProp="sameAs"
            content="https://www.youtube.com/user/Peerspace"
          />
          <meta
            itemProp="sameAs"
            content="https://www.linkedin.com/company/3542206"
          />
          <meta
            itemProp="logo"
            content="https://assets.peerspace.com/ps_logo_dark-v2.svg?v=rb"
          />
        </span>
        {children}
        <NavigationMenuViewport />
      </NavigationMenuPrimitive.Root>
    </NavigationMenuContext.Provider>
  ),
);
NavigationMenu.displayName = NavigationMenuPrimitive.Root.displayName;

type NavigationMenuListProps = React.ComponentPropsWithoutRef<
  typeof NavigationMenuPrimitive.List
>;

const NavigationMenuList = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.List>,
  NavigationMenuListProps
>(({ className, ...props }, ref) => {
  const { layout } = React.useContext(NavigationMenuContext);
  return (
    <NavigationMenuPrimitive.List
      ref={ref}
      className={cn(
        "tw-group tw-flex tw-list-none tw-gap-24",
        layout === "tabStrip"
          ? "tw-w-max tw-max-w-none tw-flex-nowrap tw-items-center tw-justify-start"
          : "tw-min-w-0 tw-w-full tw-flex-1 tw-flex-nowrap tw-items-center tw-justify-center",
        className,
      )}
      {...props}
    />
  );
});
NavigationMenuList.displayName = NavigationMenuPrimitive.List.displayName;

const NavigationMenuItem = NavigationMenuPrimitive.Item;

const navigationMenuTriggerStyle = cva(
  "tw-group tw-inline-flex tw-h-10 tw-w-max tw-items-center tw-justify-center tw-rounded-md tw-bg-background tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-transition-colors hover:tw-bg-accent hover:tw-text-accent-foreground focus:tw-bg-accent focus:tw-text-accent-foreground focus:tw-outline-none disabled:tw-pointer-events-none disabled:tw-opacity-50 data-[active]:tw-bg-accent/50 data-[state=open]:tw-bg-accent/50",
);

const NavigationMenuTrigger = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <NavigationMenuPrimitive.Trigger
    ref={ref}
    className={cn(navigationMenuTriggerStyle(), "tw-group", className)}
    {...props}
  >
    {children}{" "}
    <ChevronDown
      className="tw-relative tw-top-[1px] tw-ml-1 tw-h-3 tw-w-3 tw-transition tw-duration-200 group-data-[state=open]:tw-rotate-180"
      aria-hidden="true"
    />
  </NavigationMenuPrimitive.Trigger>
));
NavigationMenuTrigger.displayName = NavigationMenuPrimitive.Trigger.displayName;

const NavigationMenuContent = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Content>
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Content
    ref={ref}
    className={cn(
      "tw-left-0 tw-top-0 tw-w-full data-[motion^=from-]:tw-animate-in data-[motion^=to-]:tw-animate-out data-[motion^=from-]:tw-fade-in data-[motion^=to-]:tw-fade-out data-[motion=from-end]:tw-slide-in-from-right-52 data-[motion=from-start]:tw-slide-in-from-left-52 data-[motion=to-end]:tw-slide-out-to-right-52 data-[motion=to-start]:tw-slide-out-to-left-52 md:tw-absolute md:tw-w-auto",
      className,
    )}
    {...props}
  />
));
NavigationMenuContent.displayName = NavigationMenuPrimitive.Content.displayName;

export type NavigationMenuLinkProps = React.ComponentPropsWithoutRef<
  typeof NavigationMenuPrimitive.Link
> & {
  /** When `layout="tabStrip"`, sets tab selection (border-bottom, colors). Ignored for `fullWidth`. */
  selected?: boolean;
};

const NavigationMenuLink = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Link>,
  NavigationMenuLinkProps
>(({ className, selected, ...props }, ref) => {
  const { variant, layout } = React.useContext(NavigationMenuContext);
  const isTabStrip = layout === "tabStrip";

  const tabBase =
    "tw-inline-flex tw-shrink-0 tw-items-start tw-gap-8 tw-whitespace-nowrap tw-pb-16 tw-border-b-2 tw-body-1-m tw-transition-colors";

  let tabVariantClasses: string | undefined;
  if (!isTabStrip || selected === undefined) {
    tabVariantClasses = undefined;
  } else if (!selected) {
    tabVariantClasses = "tw-border-transparent tw-text-gray-500";
  } else {
    tabVariantClasses =
      variant === "white"
        ? "tw-border-black tw-text-gray-900"
        : "tw-border-white tw-text-white";
  }

  const useTabStyles = isTabStrip && selected !== undefined;

  return (
    <NavigationMenuPrimitive.Link
      ref={ref}
      className={cn(
        "tw-outline-none focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-gray-400 focus-visible:tw-ring-offset-2",
        useTabStyles
          ? tabBase
          : cn("tw-body-1-m", !isTabStrip && "tw-font-bold"),
        tabVariantClasses,
        className,
      )}
      aria-current={isTabStrip && selected ? "page" : undefined}
      {...props}
    />
  );
});
NavigationMenuLink.displayName = NavigationMenuPrimitive.Link.displayName;

const NavigationMenuViewport = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  <div
    className={cn(
      "tw-absolute tw-left-0 tw-top-full tw-flex tw-justify-center",
    )}
  >
    <NavigationMenuPrimitive.Viewport
      className={cn(
        "tw-origin-top-center tw-relative tw-mt-1.5 tw-h-[var(--radix-navigation-menu-viewport-height)] tw-w-full tw-overflow-hidden tw-rounded-md tw-border tw-bg-popover tw-text-popover-foreground tw-shadow-lg data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-90 md:tw-w-[var(--radix-navigation-menu-viewport-width)]",
        className,
      )}
      ref={ref}
      {...props}
    />
  </div>
));
NavigationMenuViewport.displayName =
  NavigationMenuPrimitive.Viewport.displayName;

const NavigationMenuIndicator = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Indicator>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Indicator>
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Indicator
    ref={ref}
    className={cn(
      "tw-top-full tw-z-[1] tw-flex tw-h-1.5 tw-items-end tw-justify-center tw-overflow-hidden data-[state=visible]:tw-animate-in data-[state=hidden]:tw-animate-out data-[state=hidden]:tw-fade-out data-[state=visible]:tw-fade-in",
      className,
    )}
    {...props}
  >
    <div className="tw-relative tw-top-[60%] tw-h-2 tw-w-2 tw-rotate-45 tw-rounded-tl-sm tw-bg-border tw-shadow-md" />
  </NavigationMenuPrimitive.Indicator>
));
NavigationMenuIndicator.displayName =
  NavigationMenuPrimitive.Indicator.displayName;

export {
  navigationMenuTriggerStyle,
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuContent,
  NavigationMenuTrigger,
  NavigationMenuLink,
  NavigationMenuIndicator,
  NavigationMenuViewport,
};
